import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ProjectLayout from '../components/layout/ProjectLayout';
import ProjectSidebar from '../components/projects/ProjectSidebar';
import { supabase } from '../supabaseClient';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../config/permissions';
import ErrorPopup from '../components/ErrorPopup';
import { AuthContext } from '../context/AuthContext';
import DocumentList from '../components/projects/DocumentList';
import { DOCUMENT_STATUSES, STORAGE, MESSAGES } from '../config/constants';

// TabPanel component to render content for each tab
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ProjectSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useContext(AuthContext);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [error, setError] = useState(null);
  const isEditing = !!id;

  // Add state for tab management
  const [currentTab, setCurrentTab] = useState(0);

  // Add state for team members
  const [projectMembers, setProjectMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Add new state for user management
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [addMemberError, setAddMemberError] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);

  const [documents, setDocuments] = useState([]);

  // Add missing states for DocumentList
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedDocs, setSelectedDocs] = useState([]);

  // Add document selection handler
  const handleDocumentSelect = (docId, isSelected) => {
    setSelectedDocs(prev => 
      isSelected 
        ? [...prev, docId]
        : prev.filter(id => id !== docId)
    );
  };

  // Handle tab change for documents
  const handleDocumentTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Add simple document update handler - Wrapped in useCallback
  const handleUpdateDocuments = useCallback((updater) => {
    // updater can be the new array directly, or a function like (prevDocs) => newDocs
    setDocuments(updater);
  }, []); // Empty dependency array means the function reference is stable

  // Define fetchProjectMembersWrapper using useCallback
  const fetchProjectMembersWrapper = useCallback(async (ignoreFlag = { current: false }) => {
    if (!id) return;

    if (!ignoreFlag.current) {
      setLoadingMembers(true);
      setError(null); // Clear previous errors to avoid confusion
    }

    try {
      // Stap 1: Haal projectleden op (user_id en project-specifieke rol)
      const { data: membersData, error: membersError } = await supabase
        .from('project_members')
        .select('user_id, role') // project-specifieke rol
        .eq('project_id', id);

      if (membersError) throw membersError;
      if (!membersData) throw new Error("No members data received from project_members table");

      // Stap 2: Haal alle gebruikersprofielen via de 'list-users' functie
      // Deze functie zou id, full_name, email, etc. moeten retourneren.
      const { data: usersFunctionResponse, error: usersFunctionError } = await supabase.functions.invoke('list-users', {
        method: 'GET'
      });

      if (usersFunctionError) throw usersFunctionError;
      
      const allUsersProfiles = usersFunctionResponse?.data || [];
      if (!Array.isArray(allUsersProfiles)) {
        console.error("Fetched users data from function is not an array:", allUsersProfiles);
        throw new Error("User data format from 'list-users' function is incorrect.");
      }

      // Stap 3: Combineer de data
      let combinedMembers = membersData.map(member => {
        const userProfile = allUsersProfiles.find(p => p && p.id === member.user_id);
        return {
          user_id: member.user_id,
          role: member.role, // Dit is de project-specifieke rol
          user: userProfile ? {
            id: userProfile.id,
            full_name: userProfile.full_name,
            email: userProfile.email,
            role: userProfile.role // Bewaar de globale rol voor filtering
          } : {
            id: member.user_id,
            full_name: 'Onbekende Gebruiker',
            email: 'Informatie niet beschikbaar',
            role: 'unknown'
          }
        };
      });

      // Filter admins uit de lijst als de huidige gebruiker een project_manager is
      if (user && user.role === 'project_manager') {
        combinedMembers = combinedMembers.filter(member => member.user.role !== 'admin');
      }

      if (!ignoreFlag.current) {
        setProjectMembers(combinedMembers);
      }

    } catch (error) {
      console.error('Error fetching project members or user profiles:', error);
      if (!ignoreFlag.current) {
        // Toon een meer generieke foutmelding, of specifiek als dat helpt
        setError(`Kon teamleden niet laden: ${error.message || 'Onbekende fout'}`);
      }
    } finally {
      if (!ignoreFlag.current) {
        setLoadingMembers(false);
      }
    }
  }, [id, supabase, setLoadingMembers, setProjectMembers, setError, user]); // Updated dependencies

  // Define fetchAvailableUsersWrapper using useCallback
  const fetchAvailableUsersWrapper = useCallback(async (currentProjectMembers, ignoreFlag = { current: false }) => {
    if (!ignoreFlag.current) {
      setLoadingAvailableUsers(true);
      setAddMemberError(''); // Clear previous errors before fetching
    }
    try {
      const { data: functionResponse, error: functionError } = await supabase.functions.invoke('list-users', {
        method: 'GET'
      });

      if (functionError) throw functionError;
      // Supabase functions might return a structured response, ensure we get the actual user data array
      const allUsers = functionResponse.data || []; 

      if (!Array.isArray(allUsers)) {
        console.error("Fetched users data is not an array:", allUsers);
        throw new Error("User data format is incorrect.");
      }
      
      // Filter out users already in the project
      const existingUserIds = currentProjectMembers.map(member => member.user_id);
      let filteredUsers = allUsers.filter(u => u && u.id && !existingUserIds.includes(u.id));
      
      // Filter out the current user and admins, similar to CreateProject.jsx
      // user comes from AuthContext
      if (user) {
        filteredUsers = filteredUsers.filter(u => u && u.id !== user.id && u.role !== 'admin');
      }
      
      if (!ignoreFlag.current) {
        setAvailableUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Error fetching available users:', error);
      if (!ignoreFlag.current) {
        setAddMemberError(`Failed to load users: ${error.message || 'Unknown error'}`);
      }
    } finally {
      if (!ignoreFlag.current) {
        setLoadingAvailableUsers(false);
      }
    }
  }, [user, setAvailableUsers, setAddMemberError, setLoadingAvailableUsers, supabase]); // Added user, supabase and relevant setters to dependencies

  // Fetch projects for sidebar
  useEffect(() => {
    const fetchProjects = async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Error fetching projects:', error);
        return;
      }
      
      setProjects(data || []);
    };

    fetchProjects();
  }, []);

  // Fetch project data if editing
  useEffect(() => {
    if (isEditing) {
      let ignore = false; // Add an ignore flag

      const fetchProjectData = async () => {
        try {
          const { data, error } = await supabase
            .from('projects')
            .select('*')
            .eq('id', id)
            .single();

          if (error) throw error;
          setFormData(data);
        } catch (error) {
          console.error('Error fetching project:', error);
          setError(error.message);
        }
      };

      fetchProjectData();

      return () => {
        ignore = true; // Set the flag to true when unmounting
      };
    }
  }, [id, isEditing]);

  // Add useEffect to fetch project members when tab changes to Team (index 2)
  useEffect(() => {
    if (currentTab === 2 && id) {
      const ignore = { current: false };
      fetchProjectMembersWrapper(ignore);
      return () => {
        ignore.current = true;
      };
    }
  }, [currentTab, id, fetchProjectMembersWrapper]); // Add fetchProjectMembersWrapper to dependencies

  // Add useEffect to fetch available users AFTER members are fetched
  useEffect(() => {
    if (currentTab === 2 && !loadingMembers) { // Fetch only when members are loaded
      const ignore = { current: false };
      fetchAvailableUsersWrapper(projectMembers, ignore); // Pass current projectMembers
      return () => {
        ignore.current = true;
      };
    }
  }, [currentTab, loadingMembers, projectMembers, fetchAvailableUsersWrapper]); // Add dependencies

  // Add effect to fetch documents when tab changes to Documents (index 1)
  useEffect(() => {
    if (currentTab === 1 && id) {
      let ignore = false;

      const fetchDocumentsWrapper = async () => {
        try {
          const { data, error } = await supabase
            .from('documents')
            .select(`
              id,
              name,
              created_at,
              storage_path,
              file_type,
              size,
              status,
              received_at,
              exported_at,
              exported_by,
              locked_by,
              profiles:locked_by (
                email,
                full_name
              )
            `)
            .eq('project_id', id)
            .order('created_at', { ascending: false });

          if (error) throw error;

          // Transform data to include lock information
          const transformedData = data.map(doc => ({
            ...doc,
            isLocked: !!doc.locked_by,
            isLockedByMe: doc.locked_by === user?.id,
            lockedByUser: doc.profiles?.full_name || doc.profiles?.email || 'Onbekend'
          }));

          setDocuments(transformedData);
        } catch (error) {
          console.error('Error fetching documents:', error);
          setError('Failed to load documents');
        }
      };

      fetchDocumentsWrapper();

      return () => {
        ignore = true;
      };
    }
  }, [currentTab, id]);

  // Add handlers for DocumentList functionality
  const handleDeleteDoc = async (docId) => {
    // Check if document is in intake status (allow admins to delete)
    const docToDelete = documents.find(doc => doc.id === docId);
    if (docToDelete && docToDelete.status === DOCUMENT_STATUSES.INTAKE && user?.role !== 'admin') {
      setError('Documenten in intake status kunnen niet worden verwijderd');
      return;
    }

    // Check if document is exported (only admins can delete exported documents)
    if (docToDelete && docToDelete.exported_at && user?.role !== 'admin') {
      setError('Geëxporteerde documenten kunnen alleen door admins worden verwijderd');
      return;
    }

    if (!window.confirm('Weet je zeker dat je dit document wilt verwijderen?')) {
      return;
    }
  
    try {
      // First get the document to get its storage_path
      const { data: doc } = await supabase
        .from('documents')
        .select('storage_path')
        .eq('id', docId)
        .single();
  
      if (doc?.storage_path) {
        // Delete the file from storage
        const { error: storageError } = await supabase.storage
          .from('documents')
          .remove([doc.storage_path]);
  
        if (storageError) throw storageError;
      }
  
      // Delete the document record from the database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', docId);
  
      if (dbError) throw dbError;
  
      // Update local state
      setDocuments(documents.filter(doc => doc.id !== docId));
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
  };
  
  const handleUploadFiles = async (files) => {
    if (!files.length) { 
      setError(MESSAGES.ERRORS.NO_FILES);
      return;
    }
  
    // Validate file types
    const invalidFiles = files.filter(file => !STORAGE.ALLOWED_TYPES.includes(file.type));
    if (invalidFiles.length > 0) {
      setError(MESSAGES.ERRORS.FILE_TYPE);
      return;
    }
  
    // Check file sizes
    const oversizedFiles = files.filter(file => file.size > STORAGE.MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setError(MESSAGES.ERRORS.FILE_SIZE);
      return;
    }
  
    try {
      for (const file of files) {
        // Upload to storage
        const fileName = `${Date.now()}-${file.name}`;
        const filePath = `${id}/${fileName}`;
  
        const { error: uploadError } = await supabase.storage
          .from(STORAGE.BUCKET_NAME)
          .upload(filePath, file, {
            contentType: file.type,
            cacheControl: '3600'
          });
  
        if (uploadError) throw uploadError;
  
        // Create document record
        const { error: dbError } = await supabase
          .from('documents')
          .insert([{
            project_id: id,
            name: file.name,
            storage_path: filePath,
            file_type: file.type,
            size: file.size,
            status: DOCUMENT_STATUSES.INTAKE,
            uploaded_by: user.id,
            received_at: new Date().toISOString()
          }]);
  
        if (dbError) throw dbError;
      }
  
      await fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      setError(MESSAGES.ERRORS.UPLOAD_FAILED);
    }
  };
  
  // Function to add a new team member - simplified to use default role
  const handleAddMember = async () => {
    if (!selectedUserId) {
      setAddMemberError('Please select a user');
      return;
    }

    setAddingMember(true);
    setAddMemberError('');

    try {
      const { error } = await supabase
        .from('project_members')
        .insert({
          project_id: id,
          user_id: selectedUserId,
          role: 'member' // Default role is now hardcoded
        });

      if (error) throw error;

      // Refresh the list after adding
      await fetchProjectMembersWrapper();
      setSelectedUserId('');
    } catch (error) {
      console.error('Error adding team member:', error);
      setAddMemberError(`Failed to add team member: ${error.message}`);
    } finally {
      setAddingMember(false);
    }
  };

  // Function to remove a team member
  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('project_members')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      // Refresh the member list and available users list
      await fetchProjectMembersWrapper();
      await fetchAvailableUsersWrapper(projectMembers.filter(m => m.user_id !== userId)); // Pass the updated list

    } catch (error) {
      console.error('Error removing team member:', error);
      setError('Failed to remove team member');
    }
  };

  // Perform permission check and redirect inside useEffect
  useEffect(() => {
    const requiredPermission = isEditing ? PERMISSIONS.EDIT_PROJECT : PERMISSIONS.CREATE_PROJECT;
    if (!hasPermission(requiredPermission)) {
      console.warn('User lacks permission, redirecting...'); // Optional: Add a log
      navigate('/projects');
    }
  }, [isEditing, hasPermission, navigate]); // Dependencies ensure this runs when needed

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        const { error } = await supabase
          .from('projects')
          .update(formData)
          .eq('id', id);

        if (error) throw error;
        navigate(`/projects/${id}`);
      } else {
        const { data, error } = await supabase
          .from('projects')
          .insert([formData])
          .select()
          .single();

        if (error) throw error;

        // Navigate back to projects page and show the newly created project
        navigate('/projects', { 
          state: { newProjectId: data.id }
        });
      }
    } catch (error) {
      console.error('Error saving project:', error);
      setError(error.message);
    }
  };

  // Update the onProjectSelect handler
  const handleProjectSelect = (project) => {
    if (isEditing) {
      // If we're in edit mode, navigate to that project's settings
      navigate(`/projects/${project.id}/settings`);
    } else {
      // If we're in create mode, just go to the project view
      navigate(`/projects/${project.id}`);
    }
  };

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleToggleStatus = async (event) => {
    const newStatus = event.target.checked ? 'active' : 'archived';
    
    try {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      // Update local state
      setFormData({ ...formData, status: newStatus });
    } catch (error) {
      console.error('Error updating project status:', error);
      setError('Failed to update project status');
    }
  };

  return (
    <ProjectLayout
      sidebar={
        <ProjectSidebar
          projects={projects}
          selectedProject={isEditing ? { id: parseInt(id) } : null}
          onProjectSelect={handleProjectSelect}
          canCreateProject={false} // Hide create button in new project view
        />
      }
      content={
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Paper sx={{ width: '100%' }}>
            {/* Tab Navigation - add Team tab */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={currentTab} onChange={handleTabChange} aria-label="project management tabs">
                <Tab label="Project Instellingen" />
                <Tab label="Documenten" />
                <Tab label="Team" />
              </Tabs>
            </Box>
            
            {/* Settings Tab */}
            <TabPanel value={currentTab} index={0}>
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography variant="h5">
                    {isEditing ? 'Project Bewerken' : 'Nieuw Project'}
                  </Typography>
                  {isEditing && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.status !== 'archived'}
                          onChange={handleToggleStatus}
                          color="primary"
                        />
                      }
                      label={formData.status === 'archived' ? 'Project Gearchiveerd' : 'Project Actief'}
                    />
                  )}
                </Box>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Projectnaam"
                    margin="normal"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="Beschrijving"
                    margin="normal"
                    multiline
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button onClick={() => navigate('/projects')}>
                      Annuleren
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained"
                      disabled={!formData.name}
                    >
                      {isEditing ? 'Project Opslaan' : 'Project Aanmaken'}
                    </Button>
                  </Box>
                </form>
              </Box>
            </TabPanel>
            
            {/* Documents Tab - Simplified */}
            <TabPanel value={currentTab} index={1}>
              <Box sx={{ p: 3 }}>
                <DocumentList
                  documents={documents}
                  onUpdateDocuments={handleUpdateDocuments}
                  onDeleteDoc={handleDeleteDoc}
                  onUploadFiles={handleUploadFiles}
                  selectedTab={selectedTab}
                  onTabChange={handleDocumentTabChange}
                  selectedDocs={selectedDocs}
                  onSelectDoc={handleDocumentSelect}
                />
              </Box>
            </TabPanel>
            
            {/* Team Tab - simplified implementation */}
            <TabPanel value={currentTab} index={2}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Project Team
                </Typography>
                
                {/* Simplified add new member form */}
                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                  <Typography variant="h6" gutterBottom>
                    Add Team Member
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <FormControl sx={{ minWidth: 300, flexGrow: 1 }}>
                      <InputLabel id="user-select-label">User</InputLabel>
                      <Select
                        labelId="user-select-label"
                        id="user-select"
                        value={selectedUserId}
                        label="User"
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        disabled={addingMember || loadingAvailableUsers}
                      >
                        <MenuItem value="">
                          <em>Select a user</em>
                        </MenuItem>
                        {availableUsers.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.full_name || u.email} ({u.email})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <Button 
                      variant="contained" 
                      startIcon={<AddCircleIcon />}
                      onClick={handleAddMember}
                      disabled={!selectedUserId || addingMember || loadingAvailableUsers}
                    >
                      {addingMember ? 'Adding...' : 'Add Member'}
                    </Button>
                  </Box>
                  
                  {addMemberError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {addMemberError}
                    </Alert>
                  )}
                </Paper>
                
                {/* Team members list remains unchanged */}
                {loadingMembers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : projectMembers.length === 0 ? (
                  <Alert severity="info">
                    No team members found for this project.
                  </Alert>
                ) : (
                  <List>
                    {projectMembers.map((member) => (
                      <React.Fragment key={`${id}_${member.user_id}`}>
                        <ListItem
                          secondaryAction={
                            <IconButton 
                              edge="end" 
                              aria-label="delete" 
                              onClick={() => handleRemoveMember(member.user_id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar>
                              {member.user?.full_name?.[0] || ''}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={member.user?.full_name || ''}
                            secondary={member.user?.email || ''}
                          />
                          <Chip 
                            label={member.role.charAt(0).toUpperCase() + member.role.slice(1)} 
                            color={member.role === 'owner' ? 'primary' : 
                                  member.role === 'admin' ? 'secondary' : 'default'}
                            size="small"
                            sx={{ ml: 2 }}
                          />
                        </ListItem>
                        <Divider variant="inset" component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </TabPanel>
          </Paper>
        </Box>
      }
    />
  );
}